import React, { useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaArrowRight, FaUser, FaPhone, FaImage } from 'react-icons/fa';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';
import { fetchDeliveryDetails, clearError, clearDeliveryDetails } from '../../../redux/Slices/AdminDeliveriesSlice';

const AdminDeliveryDetails = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { deliveryId } = useParams();
    
    const { deliveryDetails, detailsLoading, error } = useSelector((state) => state.adminDeliveries);

    useEffect(() => {
        setTitle('تفاصيل الديليفري');
        dispatch(fetchDeliveryDetails(deliveryId));
        
        return () => {
            dispatch(clearDeliveryDetails());
        };
    }, [setTitle, dispatch, deliveryId]);

    useEffect(() => {
        if (error) {
            showErrorToast(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    const getStatusBadge = (status) => {
        if (status === "1" || status === 1) {
            return <span className="badge bg-success fs-6 text-white">نشط</span>;
        }
        return <span className="badge bg-danger fs-6 text-white">غير نشط</span>;
    };

    const getBanBadge = (ban) => {
        if (ban === "1" || ban === 1) {
            return <span className="badge bg-danger fs-6 text-white">محظور</span>;
        }
        return <span className="badge bg-success fs-6 text-white">غير محظور</span>;
    };

    if (detailsLoading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل تفاصيل الديليفري...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!deliveryDetails) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <p className="text-muted">لا توجد بيانات</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">تفاصيل الديليفري</h4>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn text-white"
                        onClick={() => navigate('/admin-deliveries')}
                        style={{ backgroundColor: 'rgb(24, 65, 148)' }}>
                        <FaArrowRight className="mx-2" />
                        العودة للقائمة
                    </motion.button>
                </div>

                {/* Delivery Info Card */}
                <div className="card shadow-lg border-0 mb-4">
                    <div className="card-body p-4">
                        <div className="row">
                            {/* Basic Info */}
                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaUser className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">الاسم</h6>
                                </div>
                                <p className="fw-bold">{deliveryDetails.name || 'غير محدد'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaPhone className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">رقم الهاتف</h6>
                                </div>
                                <p className="fw-bold">{deliveryDetails.phone || 'غير محدد'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <h6 className="text-muted mb-2">الحالة</h6>
                                {getStatusBadge(deliveryDetails.status)}
                            </div>

                            <div className="col-md-6 mb-3">
                                <h6 className="text-muted mb-2">حالة الحظر</h6>
                                {getBanBadge(deliveryDetails.ban)}
                            </div>

                            <div className="col-md-6 mb-3">
                                <h6 className="text-muted mb-2">الرصيد</h6>
                                <p className={`fw-bold ${deliveryDetails.balance < 0 ? 'text-danger' : ''}`}>
                                    {deliveryDetails.balance || 0} ج.م
                                </p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <h6 className="text-muted mb-2">تاريخ التسجيل</h6>
                                <p className="fw-bold">
                                    {deliveryDetails.created_at ? new Date(deliveryDetails.created_at).toLocaleDateString('ar-EG') : 'غير محدد'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Documents */}
                <div className="card shadow-lg border-0">
                    <div className="card-header" style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}>
                        <h5 className="mb-0">
                            <FaImage className="mx-2" />
                            المستندات
                        </h5>
                    </div>
                    <div className="card-body p-4">
                        <div className="row">
                            {/* Front ID */}
                            <div className="col-md-6 mb-4">
                                <h6 className="text-muted mb-3">البطاقة الشخصية (الوجه الأمامي)</h6>
                                {deliveryDetails.front_ident ? (
                                    <img
                                        src={`https://backend.khadmatmantakty.com/${deliveryDetails.front_ident}`}
                                        alt="Front ID"
                                        className="w-100 rounded shadow"
                                        style={{ maxHeight: '300px', objectFit: 'contain', backgroundColor: '#f8f9fa' }}
                                    />
                                ) : (
                                    <div className="text-center py-5 bg-light rounded">
                                        <p className="text-muted">لا توجد صورة</p>
                                    </div>
                                )}
                            </div>

                            {/* Back ID */}
                            <div className="col-md-6 mb-4">
                                <h6 className="text-muted mb-3">البطاقة الشخصية (الوجه الخلفي)</h6>
                                {deliveryDetails.back_ident ? (
                                    <img
                                        src={`https://backend.khadmatmantakty.com/${deliveryDetails.back_ident}`}
                                        alt="Back ID"
                                        className="w-100 rounded shadow"
                                        style={{ maxHeight: '300px', objectFit: 'contain', backgroundColor: '#f8f9fa' }}
                                    />
                                ) : (
                                    <div className="text-center py-5 bg-light rounded">
                                        <p className="text-muted">لا توجد صورة</p>
                                    </div>
                                )}
                            </div>

                            {/* Driving License */}
                            <div className="col-md-6 mb-4">
                                <h6 className="text-muted mb-3">رخصة القيادة</h6>
                                {deliveryDetails.personal_deriving_license ? (
                                    <img
                                        src={`https://backend.khadmatmantakty.com/${deliveryDetails.personal_deriving_license}`}
                                        alt="Driving License"
                                        className="w-100 rounded shadow"
                                        style={{ maxHeight: '300px', objectFit: 'contain', backgroundColor: '#f8f9fa' }}
                                    />
                                ) : (
                                    <div className="text-center py-5 bg-light rounded">
                                        <p className="text-muted">لا توجد صورة</p>
                                    </div>
                                )}
                            </div>

                            {/* Vehicle License */}
                            <div className="col-md-6 mb-4">
                                <h6 className="text-muted mb-3">رخصة المركبة</h6>
                                {deliveryDetails.machine_license ? (
                                    <img
                                        src={`https://backend.khadmatmantakty.com/${deliveryDetails.machine_license}`}
                                        alt="Vehicle License"
                                        className="w-100 rounded shadow"
                                        style={{ maxHeight: '300px', objectFit: 'contain', backgroundColor: '#f8f9fa' }}
                                    />
                                ) : (
                                    <div className="text-center py-5 bg-light rounded">
                                        <p className="text-muted">لا توجد صورة</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDeliveryDetails;

